import {
  Box,
  Card,
  Col,
  ContentItem,
  ContentItems,
  ContentWrapper,
  Flex,
  Row,
  Space,
  Text,
  TitledText,
} from '@/components';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import { getNextBudgetMeasureReviewYearPinpointField } from '@/features/comment/utils/pinpoint';
import { useAccountingTypeOptions } from '@/features/master-table/hooks';
import { formatExpenditureBudgetPurpose } from '@/features/mold/utils/formatter/fund-formatter';
import { useProject } from '@/features/project/_hooks/project';
import { PROJECT_BASE_DIR } from '@/features/project/_utils/constants';
import {
  getNextBudgetMeasureReviewYearLabel,
  isBefore2025 as isBefore2025Func,
  isBefore2026 as isBefore2026Func,
} from '@/features/project/_utils/project';
import {
  ExistenceOrAbsenceLabelModifier,
  FundAdditionalAttributesModel,
  FundBudgetClassificationModifier,
  FundBudgetClassificationModifierFrom2026,
  FundBudgetTypeLabelModifier,
  FundNecessityTypeLabelModifier,
  MeansOfDeliveryLabelModifier,
  type ProjectModel,
  ReasonForNotSettingEndPeriodLabelModifier,
} from '@/features/project/projects/_models/project';
import { useId } from '@/hooks';
import { useLocaledAmount } from '@/providers';
import { FALLBACK_TEXT, formatValueByUnit } from '@/utils';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type RelatedProjectLinkProps = {
  projectId: string;
};

const RelatedProjectLink: FC<RelatedProjectLinkProps> = ({ projectId }) => {
  const { t } = useTranslation();
  const project = useProject(projectId);
  if (!project.data) {
    return null;
  }

  return (
    <TitledText
      variant="link"
      href={`${PROJECT_BASE_DIR}/${projectId}`}
      title={t('project-name')}
      text={project.data.name}
    />
  );
};

type Props = z.infer<typeof ProjectModel>;

export const FundAdditionalAttributesStandard: FC<Props> = (project) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const accountOptions = useAccountingTypeOptions();
  const parsed = FundAdditionalAttributesModel.safeParse(
    project.fundAdditionalAttributes,
  );
  const { id: projectId } = useId();
  const isBefore2025 = useMemo(() => isBefore2025Func(project), [project]);
  const fundBudgetClassificationModifier = useMemo(
    () =>
      isBefore2026Func(project)
        ? FundBudgetClassificationModifier
        : FundBudgetClassificationModifierFrom2026,
    [project],
  );
  const nextBudgetMeasureReviewYearPinpointField = useMemo(
    () => getNextBudgetMeasureReviewYearPinpointField(project),
    [project],
  );

  if (!parsed.success) {
    return null;
  }

  return (
    <Flex vertical gap={24}>
      {isBefore2025 && (
        <ContentWrapper title={t('necessity-of-fund-method')}>
          <ContentItem borderBottom={0}>
            <ContentItems
              items={[
                <TitledText
                  key="fund-necessity-type"
                  title={t('fund-necessity-type')}
                  text={
                    parsed.data.necessityType &&
                    FundNecessityTypeLabelModifier[parsed.data.necessityType]
                  }
                />,
                <TitledText
                  key="fund-necessity-reasons"
                  title={t('fund-necessity-reasons')}
                  text={parsed.data.necessityReasons}
                />,
              ]}
            />
          </ContentItem>
        </ContentWrapper>
      )}
      <ContentWrapper
        title={
          <PinpointMarker
            pinpoint={{
              field: 'ks.standard.composition-backgrounds-title',
              path: { project: projectId },
            }}
          >
            <Text variant="h3" text={t('composition-backgrounds')} />
          </PinpointMarker>
        }
      >
        <ContentItem borderBottom={0}>
          {parsed.data.compositionBackgrounds?.length ? (
            <Row gutter={[24, 24]}>
              {parsed.data.compositionBackgrounds.map((item, index) => {
                const formattedExpenditureBudgetPurposes =
                  formatExpenditureBudgetPurpose(
                    item.expenditureBudgetPurposes,
                  );
                return (
                  <Col span={12} key={index.toString()}>
                    <Card>
                      <Row gutter={[16, 24]}>
                        <Col span={12}>
                          <TitledText
                            title={t('fiscal-year-of-budgetary-provision')}
                            text={item.fiscalYearOfBudgetaryProvision}
                          />
                        </Col>
                        <Col span={12}>
                          <TitledText
                            title={
                              index > 0
                                ? t('added-fiscal-year')
                                : t('fiscal-year-of-creation')
                            }
                            text={item.fiscalYearOfCreation}
                          />
                        </Col>
                        <Col span={12}>
                          <TitledText
                            title={t('means-of-delivery')}
                            text={
                              item.meansOfDelivery &&
                              MeansOfDeliveryLabelModifier[item.meansOfDelivery]
                            }
                          />
                        </Col>
                        <Col span={12}>
                          <TitledText
                            title={t('accounting-type')}
                            text={
                              accountOptions.data?.find(
                                (option) => option.value === item.accountingId,
                              )?.label
                            }
                          />
                        </Col>
                        <Col span={12}>
                          <TitledText
                            title={t('fund-budget-type')}
                            text={
                              item.budget &&
                              FundBudgetTypeLabelModifier[item.budget]
                            }
                          />
                        </Col>
                        <Col span={24}>
                          <Space direction="vertical">
                            <Text
                              color="secondary"
                              text={t('expenditure-budget-name')}
                            />
                            {formattedExpenditureBudgetPurposes?.length ? (
                              <>
                                {formattedExpenditureBudgetPurposes.map(
                                  (expenditureBudgetPurpose, index) => {
                                    return (
                                      <Box
                                        key={index.toString()}
                                        borderBottom="1px solid #DADADA"
                                        borderColor="border"
                                        paddingBottom={8}
                                      >
                                        <Text
                                          weight="regular"
                                          text={expenditureBudgetPurpose}
                                        />
                                      </Box>
                                    );
                                  },
                                )}
                              </>
                            ) : (
                              <Text text={null} />
                            )}
                          </Space>
                        </Col>
                        <Col span={24}>
                          <TitledText
                            title={t('expenditure-budget-name-for-old')}
                            text={item.expenditurePurposeName}
                          />
                        </Col>
                        <Col span={12}>
                          <TitledText
                            title={t(
                              'amount-of-state-expenditure-with-thousand-yen',
                            )}
                            text={formatValueByUnit(
                              item.amountOfStateExpenditure,
                              {
                                operation: 'round',
                                unit,
                              },
                            )}
                          />
                        </Col>
                        <Col span={12}>
                          <TitledText
                            title={t(
                              'have-applied-law-of-appropriateness-of-subsidies',
                            )}
                            text={
                              typeof item.haveAppliedLawOfAppropriatenessOfSubsidies ===
                              'boolean'
                                ? ExistenceOrAbsenceLabelModifier[
                                    item.haveAppliedLawOfAppropriatenessOfSubsidies.toString()
                                  ]
                                : item.haveAppliedLawOfAppropriatenessOfSubsidies
                            }
                          />
                        </Col>
                        <Col span={24}>
                          <Row gutter={[16, 8]}>
                            <Col span={24}>
                              <Text
                                text={t('related-review-sheet')}
                                variant="body"
                                color="secondary"
                              />
                            </Col>
                            <Col span={12}>
                              <TitledText
                                title={t('created-year')}
                                text={item.relatedReviewSheetYear ?? ''}
                              />
                            </Col>
                            <Col span={12}>
                              {item.relatedReviewSheetId ? (
                                <RelatedProjectLink
                                  projectId={item.relatedReviewSheetId}
                                />
                              ) : (
                                <TitledText
                                  title={t('project-name')}
                                  text={item.relatedReviewSheetName ?? ''}
                                />
                              )}
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          ) : (
            FALLBACK_TEXT
          )}
        </ContentItem>
      </ContentWrapper>

      {!isBefore2025 && (
        <ContentWrapper title={t('budget-measure-for-fund')}>
          <ContentItem borderBottom={0} paddingBottom={0}>
            <ContentItems
              items={[
                <PinpointMarker
                  key="fundBudgetClassification"
                  pinpoint={{
                    field: 'ks.standard.fund-budget-classification',
                    path: { project: projectId },
                  }}
                >
                  <TitledText
                    title={t('classification-of-the-fund')}
                    text={
                      project.fundAdditionalAttributes
                        ?.fundBudgetClassification &&
                      fundBudgetClassificationModifier[
                        project.fundAdditionalAttributes
                          .fundBudgetClassification
                      ]
                    }
                  />
                </PinpointMarker>,
                <PinpointMarker
                  key="recentBudgetMeasureYear"
                  pinpoint={{
                    field: 'ks.standard.recent-budget-measure-year',
                    path: { project: projectId },
                  }}
                >
                  <TitledText
                    title={t('latest-budget-year')}
                    text={
                      project.fundAdditionalAttributes?.recentBudgetMeasureYear
                    }
                  />
                </PinpointMarker>,
              ]}
            />
          </ContentItem>
          <ContentItem borderBottom={0} paddingBottom={0}>
            <ContentItems
              items={[
                <PinpointMarker
                  key="budgetReasonWithAchievementStatusAndReviewResult"
                  pinpoint={{
                    field:
                      'ks.standard.budget-reason-with-achievement-status-and-review-result',
                    path: { project: projectId },
                  }}
                >
                  <TitledText
                    key="budgetReasonWithAchievementStatusAndReviewResult"
                    title={t('budget-reason-and-outcome')}
                    text={
                      project.fundAdditionalAttributes
                        ?.budgetReasonWithAchievementStatusAndReviewResult
                    }
                  />
                </PinpointMarker>,
                <PinpointMarker
                  key="nextBudgetMeasureReviewYear"
                  pinpoint={{
                    field: nextBudgetMeasureReviewYearPinpointField,
                    path: { project: projectId },
                  }}
                >
                  <TitledText
                    title={getNextBudgetMeasureReviewYearLabel(project)}
                    text={
                      project.fundAdditionalAttributes
                        ?.nextBudgetMeasureReviewYear
                    }
                  />
                </PinpointMarker>,
              ]}
            />
          </ContentItem>
        </ContentWrapper>
      )}

      <ContentWrapper
        title={
          <PinpointMarker
            pinpoint={{
              field: 'ks.standard.national-treasury-return-background-title',
              path: { project: projectId },
            }}
          >
            <Text
              variant="h3"
              text={t('national-treasury-return-backgrounds')}
            />
          </PinpointMarker>
        }
      >
        <ContentItem borderBottom={0}>
          {parsed.data.nationalTreasuryReturnBackgrounds?.length ? (
            <Row gutter={[24, 24]}>
              {parsed.data.nationalTreasuryReturnBackgrounds.map(
                (item, idx) => {
                  return (
                    <Col
                      span={12}
                      key={`national-treasury-return-backgrounds-row-${idx.toString()}`}
                    >
                      <Card>
                        <Row gutter={[24, 24]}>
                          <Col span={12}>
                            <TitledText
                              title={t(
                                'fiscal-year-of-national-treasury-return',
                              )}
                              text={item.fiscalYear}
                            />
                          </Col>
                          <Col span={12}>
                            <TitledText
                              title={t(
                                'amount-of-national-treasury-return-with-thousand-yen',
                              )}
                              text={formatValueByUnit(item.amount, {
                                unit,
                                operation: 'round',
                              })}
                            />
                          </Col>
                          <Col span={24}>
                            <TitledText
                              title={t('reason-of-national-treasury-return')}
                              text={item.reasons}
                            />
                          </Col>
                        </Row>
                      </Card>
                    </Col>
                  );
                },
              )}
            </Row>
          ) : (
            FALLBACK_TEXT
          )}
        </ContentItem>
      </ContentWrapper>
      {!isBefore2025 && (
        <ContentWrapper
          title={t(
            'effort-and-result-overview-of-reviews-carried-out-in-the-past',
          )}
        >
          <ContentItem borderBottom={0}>
            <PinpointMarker
              pinpoint={{
                field: 'ks.standard.effort-and-result-overview',
                path: { project: projectId },
              }}
            >
              <Text text={parsed.data.effortAndResultAndOverview} />
            </PinpointMarker>
          </ContentItem>
        </ContentWrapper>
      )}
      {isBefore2025 && (
        <ContentWrapper title={t('scheduled-end-time')}>
          <ContentItem borderBottom={0} paddingBottom={0}>
            <ContentItems
              items={[
                <TitledText
                  key="fund-scheduled-end-period"
                  title={t('fund-scheduled-end-period')}
                  text={parsed.data.scheduledEnd?.endDate}
                />,
                <TitledText
                  key="reason-for-not-setting-scheduled-end-period"
                  title={t('reason-for-not-setting-scheduled-end-period')}
                  text={
                    parsed.data.scheduledEnd
                      ?.reasonsForNotSettingScheduledEndPeriod &&
                    ReasonForNotSettingEndPeriodLabelModifier[
                      parsed.data.scheduledEnd
                        .reasonsForNotSettingScheduledEndPeriod
                    ]
                  }
                />,
              ]}
            />
          </ContentItem>

          <ContentItem>
            <ContentItems
              items={[
                <TitledText
                  key="supplementary-reasons"
                  title={t('supplementary-reasons')}
                  text={
                    parsed.data.scheduledEnd?.supplementaryReasonsForEndPeriod
                  }
                />,
                <TitledText
                  key="reason-for-changing-scheduled-end-period"
                  title={t('reason-for-changing-scheduled-end-period')}
                  text={
                    parsed.data.scheduledEnd
                      ?.reasonsForChangingScheduledEndPeriod
                  }
                />,
              ]}
            />
          </ContentItem>

          <ContentItem borderBottom={0} paddingBottom={0}>
            <ContentItems
              items={[
                <TitledText
                  key="end-date-of-new-application"
                  title={t('end-date-of-new-application')}
                  text={
                    parsed.data.scheduledEnd?.endDateOfNewApplicationReceiving
                  }
                />,
                <TitledText
                  key="reason-for-not-setting-end-date-of-new-application"
                  title={t(
                    'reason-for-not-setting-end-date-of-new-application',
                  )}
                  text={
                    parsed.data.scheduledEnd
                      ?.reasonsForNotSettingEndPeriodOfNewApplicationReceiving &&
                    ReasonForNotSettingEndPeriodLabelModifier[
                      parsed.data.scheduledEnd
                        .reasonsForNotSettingEndPeriodOfNewApplicationReceiving
                    ]
                  }
                />,
              ]}
            />
          </ContentItem>

          <ContentItem borderBottom={0} paddingBottom={0}>
            <ContentItems
              items={[
                <TitledText
                  key="supplementary-reasons"
                  title={t('supplementary-reasons')}
                  text={
                    parsed.data.scheduledEnd
                      ?.supplementaryReasonsForNewApplicationReceiving || null
                  }
                />,
                <TitledText
                  key="reason-for-changing-end-date-of-new-application"
                  title={t('reason-for-changing-end-date-of-new-application')}
                  text={
                    parsed.data.scheduledEnd
                      ?.reasonsForChangingEndPeriodOfNewApplicationReceiving
                  }
                />,
              ]}
            />
          </ContentItem>
        </ContentWrapper>
      )}

      <ContentWrapper
        title={t('matter-of-act-for-normalization-of-grant')}
        borderBottom={0}
      >
        <ContentItem borderBottom={0}>
          <ContentItems
            items={[
              <PinpointMarker
                key="matter-of-act-for-normalization-of-grant"
                pinpoint={{
                  field: 'ks.standard.matter-of-act-for-normalization-of-grant',
                  path: { project: projectId },
                }}
              >
                <Text
                  key="matter-of-act-for-normalization-of-grant"
                  text={parsed.data.matterOfActForNormalizationOfGrant}
                  weight="regular"
                />
              </PinpointMarker>,
            ]}
          />
        </ContentItem>
      </ContentWrapper>
    </Flex>
  );
};
